import CONSTANTS from "core/constants";
import { uniqBy } from "lodash";
import { TFunction } from "react-i18next";

import {
  isErrorWithCode,
  isErrorWithMessage,
  isErrorWithStatus,
} from "api/helpers";
import {
  QuestionnaireBasic,
  QuestionnaireRequest,
  QuestionnaireRequestApi,
  UserAnswer,
  UserAnswerRequestApi,
} from "core/types/index";
import { format } from "date-fns";
import { groupBy, indexBy, reduce } from "remeda";

export type QuestionnairesBasicInfo = {
  questionnaires: QuestionnaireBasic[];
  options: { optionId: string; score: number }[];
};

function getTooltipText(
  step: QuestionnaireBasic["scoreSteps"][0] | undefined,
  userAnswerError: unknown,
  isAnswered: boolean | undefined,
  t: TFunction,
  noAccess: boolean,
): string {
  if (!isAnswered) return t("NO_RESPONSE");

  if (noAccess) {
    return isErrorWithMessage(userAnswerError, "Not Authorized")
      ? t("NOT_PATIENT_QUESTIONNAIRES_ERROR")
      : t("NOT_CONSENTED_QUESTIONNAIRES_ERROR");
  }

  return step?.stepText ?? "";
}

export const calculateScores = (
  t: TFunction,
  answers?: UserAnswer[],
  basicInfoOptions?: {
    optionId: string;
    score: number;
  }[],
  scoreSteps?: QuestionnaireBasic["scoreSteps"],
  userAnswerError?: unknown,
  isAnswered?: boolean,
) => {
  const maxScore = Math.max(
    ...(scoreSteps?.map((step) => step.boundary) || []),
  );

  const score = uniqBy(answers, "questionId").reduce((sum, answer) => {
    const id = answer?.answerId;
    const optionScore = basicInfoOptions?.find(
      (option) => option?.optionId === id,
    )?.score;
    return sum + (optionScore ?? 0);
  }, 0);

  const step = [...(scoreSteps ?? [])]
    ?.sort((a, b) => a.boundary - b.boundary)
    .find((stepz) => score <= stepz.boundary);

  const noAccess =
    isErrorWithCode(userAnswerError, 403) ||
    isErrorWithStatus(userAnswerError, 403);

  const tooltip = getTooltipText(
    step,
    userAnswerError,
    isAnswered,
    t,
    noAccess,
  );

  const status = noAccess || !isAnswered ? undefined : step?.statusColor.name;
  let renderInfoIcon = false;

  let value;
  if (!isAnswered) {
    value = "--";
  } else if (noAccess) {
    value = t("NO_ACCESS");
    renderInfoIcon = true;
  } else {
    value = `${score}/${maxScore}`;
  }
  return { value, tooltip, status, renderInfoIcon };
};

export const mapQuestionnaireRequest = ({
  id,
  user_id: userId,
  clinical_id: clinicalId,
  requesting_psychologist_id: requestingPsychologistId,
  requested_at: requestedAt,
  created_at: createdAt,
  completed_at: completedAt,
  psychologist_seen_at: psychologistSeenAt,
  automatically_sent_at: automaticallySentAt,
  automatically_sent_for_slot_id: automaticallySentForSlotId,
  external_source: externalSource,
}: QuestionnaireRequestApi): QuestionnaireRequest => ({
  id,
  userId,
  clinicalId,
  requestingPsychologistId,
  requestedAt,
  createdAt,
  completedAt,
  psychologistSeenAt,
  automaticallySentAt,
  automaticallySentForSlotId,
  externalSource,
});

export const mapUserAnswerRequest = ({
  id,
  answer_id: answerId,
  created_at: createdAt,
  group_id: groupId,
  question_id: questionId,
  survey_id: surveyId,
  request_id: requestId,
  icbt_request_id: icbtRequestId,
}: UserAnswerRequestApi): UserAnswer => ({
  id,
  answerId,
  createdAt,
  groupId,
  questionId,
  surveyId,
  requestId: (requestId ?? String(icbtRequestId)) as string,
});

export const getSentByFromQuestionnaireRequest = (
  questionnaireRequest: QuestionnaireRequest,
  t: TFunction,
) => {
  if (questionnaireRequest.externalSource) {
    return t(
      `QUESTIONNAIRE_REQUEST_EXTERNAL_SOURCE.${questionnaireRequest.externalSource}`,
      questionnaireRequest.externalSource,
    );
  }
  if (
    questionnaireRequest.automaticallySentAt ||
    questionnaireRequest.automaticallySentForSlotId
  ) {
    return t("AUTOMATIC");
  }
  return questionnaireRequest.requestingPsychologistName;
};

export const createQuestionnaireDashboardData = ({
  questionnaireRequests,
  patientQuestionnaireAnswers,
  questionnaireBasicInfo,
}: {
  questionnaireRequests: QuestionnaireRequest[];
  patientQuestionnaireAnswers?: UserAnswer[];
  questionnaireBasicInfo: QuestionnairesBasicInfo;
}) => {
  const answersGroupedByRequestId = groupBy(
    patientQuestionnaireAnswers ?? [],
    (a) => a.requestId,
  );

  const patchedQuestionnaireRequests = questionnaireRequests.map((request) => {
    const isAnswered = !!request.completedAt;
    const isSeen = !!request.psychologistSeenAt;
    const isNew =
      isAnswered &&
      (!isSeen ||
        new Date(request.completedAt as string).getTime() >
          new Date(request.psychologistSeenAt as string).getTime());

    return {
      ...request,
      userAnswers: answersGroupedByRequestId[request.id] ?? [],
      isNew,
    };
  });

  const indexedQuestionnaireAnswers = indexBy(
    questionnaireBasicInfo.options,
    (o) => o.optionId,
  );

  const patchedQuestionnaireRequestsWithScore = patchedQuestionnaireRequests
    .filter((request) => request.userAnswers.length > 0)
    .map((request) => {
      const score = reduce(
        request.userAnswers,
        (acc, answer) => {
          return (
            acc + (indexedQuestionnaireAnswers[answer.answerId]?.score ?? 0)
          );
        },
        0,
      );

      return {
        ...request,
        score,
      };
    });

  const patchedQuestionnaireRequestsGroupedByClinicalId = groupBy(
    patchedQuestionnaireRequestsWithScore,
    (r) => r.clinicalId,
  );

  return patchedQuestionnaireRequestsGroupedByClinicalId;
};

export type CreateQuestionnaireDashboardData = ReturnType<
  typeof createQuestionnaireDashboardData
>;

export const createQuestionnaireTableData = ({
  questionnaireRequests,
  patientQuestionnaires,
  questionnaireBasicInfo,
  userAnswerError,
  t,
}: {
  questionnaireRequests: QuestionnaireRequest[];
  patientQuestionnaires?: UserAnswer[];
  questionnaireBasicInfo: QuestionnairesBasicInfo;
  userAnswerError?: unknown;
  t: TFunction;
}) => {
  const completed = questionnaireRequests
    .filter((r) => r.completedAt)
    .sort(
      (a, b) =>
        new Date(b.completedAt || "").getTime() -
        new Date(a.completedAt || "").getTime(),
    );
  const rest = questionnaireRequests
    .filter((r) => !r.completedAt)
    .sort(
      (a, b) =>
        new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime(),
    );
  const sorted = [...rest, ...completed];

  return (
    sorted?.map((request) => {
      const answers = patientQuestionnaires?.filter(
        (pQ) => pQ.requestId === request.id,
      );
      const basicInfo = questionnaireBasicInfo?.questionnaires?.find(
        (q) => q.clinicalId === request.clinicalId,
      );
      const isAnswered = !!request.completedAt;

      const { value, tooltip, status, renderInfoIcon } = calculateScores(
        t,
        answers,
        questionnaireBasicInfo?.options,
        basicInfo?.scoreSteps,
        userAnswerError,
        isAnswered,
      );
      const isSeen = !!request.psychologistSeenAt;

      const isNew =
        isAnswered &&
        (!isSeen ||
          new Date(request.completedAt as string).getTime() >
            new Date(request.psychologistSeenAt as string).getTime());

      return {
        id: request.id,
        questionnaireBasicInfo: basicInfo,
        highlightRow: isNew,
        clinicalId: request?.clinicalId,
        title: {
          value: `${request?.clinicalId}: ${basicInfo?.clinicalName}`,
          status: isNew ? "STATUS_NEW" : undefined,
          tooltip: "",
        },
        dateSent: format(
          new Date(request?.requestedAt ?? ""),
          CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
        ),
        sentBy: getSentByFromQuestionnaireRequest(request, t),
        score: {
          tooltip,
          status: isAnswered && status ? `STATUS_${status}` : undefined,
          value,
          renderInfoIcon,
        },
        dateCompleted: request?.completedAt
          ? format(
              new Date(request?.completedAt ?? ""),
              CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
            )
          : "--",
        userAnswer: answers,
        isNew,
        rowMetadata: isNew
          ? {
              status: "info" as const,
            }
          : undefined,
      };
    }) || []
  );
};

export const handleQuestionnaireError = (
  error: unknown,
  notAuthorizedKey: string,
  enqueueSnackbar: (item: { text: string; isError: boolean }) => void,
  t: TFunction,
) => {
  enqueueSnackbar({
    text: isErrorWithMessage(error, "Not Authorized")
      ? t(notAuthorizedKey)
      : t("SOMETHING_WENT_WRONG"),
    isError: true,
  });
};
